import { useTranslation } from "react-i18next";
import { useState } from "react";
import { GetPreferenceResponse } from "../../types/api/api-types";
import { PreferenceLine } from "./preferenceLine";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  CotationPreferenceData,
  CotationPreferenceSchema,
} from "../../types/forms/preferences/cotationPreference.form";
import { zodResolver } from "@hookform/resolvers/zod";
import StyledSwitch from "../ui/customs/StyledSwitch";
import { StyledSelectSingleValueInt } from "../styled/StyledSelectSingleValueInt";
import { usePatchPreference } from "../../api/preference.api";
import { toast } from "react-toastify";
import { OtherFeesModal } from "./otherFeesModal";
import { useGetCompany } from "../../api/company.api";

interface PreferencesCotationProps {
  preference: GetPreferenceResponse;
}

export const PreferencesCotation = ({ preference }: PreferencesCotationProps) => {
  const { t } = useTranslation(["boClients", "common"]);
  const newWinePreference = useForm<CotationPreferenceData>({
    defaultValues: {
      insuranceToggleDefault: preference.insuranceToggleDefault,
      woodBoxesToggle: preference.woodBoxesToggle,
      woodBoxesDefault: preference.woodBoxesDefault,
      otherFeesToggle: preference.otherFeesToggle,
      winePriceDisplayToggle: preference.winePriceDisplayToggle,
      optionDapToggle: preference.optionDapToggle,
    },
    resolver: zodResolver(CotationPreferenceSchema),
    mode: "onChange",
  });
  const { mutateAsync: patchPreference } = usePatchPreference();
  const [editing, setEditing] = useState(false);
  const [otherFeesModal, setOtherFeesModal] = useState(false);

  const handleSubmit = async () => {
    newWinePreference.handleSubmit(onSubmit)();
    setEditing(false);
  };

  const onSubmit: SubmitHandler<CotationPreferenceData> = async (data) => {
    await patchPreference({
      ...data,
    });
    toast.success(t("profilePreferencesCotation.saveSuccess"));
  };

  const handleCancel = () => {
    setEditing(false);
    newWinePreference.reset();
  };

  const handleEdit = () => {
    setEditing(true);
  };

  const { data: company } = useGetCompany();

  return (
    <div className="flex flex-col gap-2 lg:px-8">
      <div className="flex flex-row justify-between gap-2 border-0 border-b border-gray-200 pb-5">
        <div className="items-left flex flex-col gap-2">
          <h2 className="text-lg font-semibold">{t("profilePreferencesCotation.title")}</h2>
          <p className="text-sm text-gray-600">{t("profilePreferencesCotation.subtitle")}</p>
        </div>
        {editing ? (
          <div className="flex flex-col-reverse gap-4 md:flex-row">
            <button
              className="h-fit rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 hover:text-gray-800"
              onClick={handleCancel}
            >
              {t("common:actions.cancel")}
            </button>
            <button
              className="bg-brand700 hover:bg-brand600 flex h-fit flex-row gap-1 rounded-lg px-3 py-2 text-sm font-semibold text-white"
              onClick={handleSubmit}
            >
              <span className="md:hidden">{t("profilePreferencesCotation.submitShort")}</span>
              <span className="hidden md:block">{t("profilePreferencesCotation.submitLong")}</span>
            </button>
          </div>
        ) : (
          <button
            onClick={handleEdit}
            className="h-fit rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 hover:text-gray-800"
          >
            {t("profilePreferencesCotation.edit")}
          </button>
        )}
      </div>
      <div className="flex flex-col gap-4">
        {editing ? (
          <form className="flex flex-col gap-0">
            <div className="flex flex-row items-end justify-between border-0 border-b border-gray-200 py-4">
              <div className="flex flex-1 flex-col">
                <p className="text-sm font-medium text-gray-600">
                  {t("profilePreferencesCotation.insuranceLabel")}
                </p>
              </div>
              <div className="flex flex-1 flex-col">
                <div className="flex flex-row gap-3">
                  <StyledSwitch name="insuranceToggleDefault" control={newWinePreference.control} />
                  <p className="text-base font-medium text-gray-700">
                    {newWinePreference.watch("insuranceToggleDefault")
                      ? t("profilePreferencesCotation.enabled")
                      : t("profilePreferencesCotation.disabled")}
                  </p>
                </div>
              </div>
            </div>
            {company?.optionDAPActive && (
              <div className="flex flex-row items-end justify-between border-0 border-b border-gray-200 py-4">
                <div className="flex flex-1 flex-col">
                  <p className="text-sm font-medium text-gray-600">
                    {t("profilePreferencesCotation.dapLabel")}
                  </p>
                </div>
                <div className="flex flex-1 flex-col">
                  <div className="flex flex-row gap-3">
                    <StyledSwitch name="optionDapToggle" control={newWinePreference.control} />
                    <p className="text-base font-medium text-gray-700">
                      {newWinePreference.watch("optionDapToggle")
                        ? t("profilePreferencesCotation.enabled")
                        : t("profilePreferencesCotation.disabled")}
                    </p>
                  </div>
                </div>
              </div>
            )}
            <div className="flex flex-row items-start justify-between border-0 border-b border-gray-200 py-4">
              <div className="flex flex-1 flex-col">
                <p className="text-sm font-medium text-gray-600">
                  {t("profilePreferencesCotation.woodBoxDefaultLabel")}
                </p>
              </div>
              <div className="flex flex-1 flex-col gap-4">
                <StyledSelectSingleValueInt
                  control={newWinePreference.control}
                  name="woodBoxesDefault"
                  title={t("profilePreferencesCotation.woodBoxBottlesLabel")}
                  options={[
                    { label: "6", value: 6 },
                    { label: "12", value: 12 },
                  ]}
                  setValue={(e: any) => newWinePreference.setValue("woodBoxesDefault", e)}
                  className="w-fit"
                />
                <div className="flex flex-row gap-3">
                  <StyledSwitch name="woodBoxesToggle" control={newWinePreference.control} />
                  <span className="text-base font-medium text-gray-700">
                    {newWinePreference.watch("woodBoxesToggle")
                      ? t("profilePreferencesCotation.enabled")
                      : t("profilePreferencesCotation.disabled")}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex flex-row items-start justify-between border-0 border-b border-gray-200 py-4">
              <div className="flex flex-1 flex-col">
                <p className="text-sm font-medium text-gray-600">
                  {t("profilePreferencesCotation.winePriceDisplayLabel")}
                </p>
              </div>
              <div className="flex flex-1 flex-col">
                <div className="flex flex-row gap-3">
                  <StyledSwitch name="winePriceDisplayToggle" control={newWinePreference.control} />
                  <p className="text-base font-medium text-gray-700">
                    {newWinePreference.watch("winePriceDisplayToggle")
                      ? t("profilePreferencesCotation.enabled")
                      : t("profilePreferencesCotation.disabled")}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex flex-row items-start justify-between border-0 border-b border-gray-200 py-4">
              <div className="flex flex-1 flex-col">
                <p className="text-sm font-medium text-gray-600">
                  {t("profilePreferencesCotation.otherFeesLabel")}
                </p>
              </div>
              <div className="flex flex-1 flex-col gap-4">
                <div className="flex flex-row gap-3">
                  <StyledSwitch name="otherFeesToggle" control={newWinePreference.control} />
                  <p className="text-base font-medium text-gray-700">
                    {newWinePreference.watch("otherFeesToggle")
                      ? t("profilePreferencesCotation.enabled")
                      : t("profilePreferencesCotation.disabled")}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setOtherFeesModal(!otherFeesModal)}
                  className="text-brand700 hover:text-brand600 h-fit w-fit bg-transparent font-semibold focus:outline-hidden"
                >
                  {t("profilePreferencesCotation.completeOtherFees")}
                </button>
              </div>
            </div>
          </form>
        ) : (
          <div className="flex flex-col gap-0">
            <PreferenceLine
              title={t("profilePreferencesCotation.insuranceLabel")}
              value={
                preference.insuranceToggleDefault
                  ? [t("profilePreferencesCotation.enabled")]
                  : [t("profilePreferencesCotation.disabled")]
              }
            />
            {company?.optionDAPActive && (
              <PreferenceLine
                title={t("profilePreferencesCotation.dapLabel")}
                value={
                  preference.optionDapToggle
                    ? [t("profilePreferencesCotation.enabled")]
                    : [t("profilePreferencesCotation.disabled")]
                }
              />
            )}
            <PreferenceLine
              title={t("profilePreferencesCotation.woodBoxLabel")}
              value={[
                t("profilePreferencesCotation.woodBoxBottles", {
                  count: preference.woodBoxesDefault,
                }),
                preference.woodBoxesToggle
                  ? t("profilePreferencesCotation.enabled")
                  : t("profilePreferencesCotation.disabled"),
              ]}
            />
            <PreferenceLine
              title={t("profilePreferencesCotation.winePriceDisplayReadLabel")}
              value={
                preference.winePriceDisplayToggle
                  ? [t("profilePreferencesCotation.enabled")]
                  : [t("profilePreferencesCotation.disabled")]
              }
            />
            <PreferenceLine
              title={t("profilePreferencesCotation.otherFeesLabel")}
              value={
                preference.otherFeesToggle
                  ? [t("profilePreferencesCotation.enabled")]
                  : [t("profilePreferencesCotation.disabled")]
              }
            />
          </div>
        )}
      </div>
      {otherFeesModal && (
        <OtherFeesModal closeModal={() => setOtherFeesModal(false)} preference={preference} />
      )}
    </div>
  );
};
